import React, { useEffect, type ComponentProps, type ReactElement } from "react";
import { Link } from "@tanstack/react-router";
import type { RdScheduleAppointment } from "../../../../shared/rd-schedule";
import { useFeatures } from "@/lib/use-features";
import { showsNotes, type RenderPatientLink } from "./appointment-details-body";
import { useActionOptions } from "./actions/use-action-options";
import { useScheduleApi } from "./actions/use-action-submit";
import { AppointmentDetailsPanel, panelFlags } from "./appointment-details-panel";
import {
  PENDING_NOTE_WINDOW_MS,
  clearPendingNote,
  pendingNoteStatus,
  usePendingNote,
} from "../../lib/rd-schedule-notes";

// NO-875. The panel as the Week view mounts it: Open patient page is a router
// link, and the `rd-schedule` and `patient-detail` flags come from the server's
// flag map through `panelFlags` (tested). The Week view (NO-874) passes the
// clicked block's appointment and the action callbacks. The Note section reads
// the visit's action options each time the panel opens on a visit whose note
// can be edited (scheduled or checked in). A save answers before Healthie has
// the note, so the note the RD saved shows with "Saving to Healthie…" and the
// read repeats for a while, until a read returns it or the window runs out.

const renderPatientLink: RenderPatientLink = ({ patientId, className, children }) => (
  <Link to="/patients/$patientId" params={{ patientId }} className={className}>
    {children}
  </Link>
);

/** A visit whose note the panel reads (none selected, or one with no note, reads nothing). */
function hasNote(appointment: RdScheduleAppointment | null): boolean {
  return appointment !== null && showsNotes(appointment);
}

export function ConnectedAppointmentDetailsPanel(
  props: Omit<
    ComponentProps<typeof AppointmentDetailsPanel>,
    | "rdScheduleEnabled"
    | "patientDetailEnabled"
    | "renderPatientLink"
    | "notes"
    | "savingNote"
    | "onReloadNotes"
  > & {
    /** The RD a supervisor or admin acts for; null for an RD acting on themself. */
    rdId?: string | null;
  }
): ReactElement | null {
  const features = useFeatures();
  const api = useScheduleApi();
  const { appointment, open, rdId, ...rest } = props;
  const flags = panelFlags(features.data?.data);
  const appointmentId = appointment?.id ?? "";
  const pending = usePendingNote(appointmentId);
  const enabled = flags.rdScheduleEnabled && open && hasNote(appointment);
  const { state, reload } = useActionOptions(
    api,
    rdId ?? null,
    appointmentId,
    enabled,
    enabled && pending !== null
  );
  const read = state.status === "ready" ? state.options.current.privateNote : undefined;
  const status = pendingNoteStatus(pending, read, Date.now());
  const settled = status === "settled";

  // Healthie returned the saved note, or the window ran out.
  useEffect(() => {
    if (settled) clearPendingNote(appointmentId);
  }, [settled, appointmentId]);

  // Nothing reads after the polling stops, so the window ends on its own clock.
  useEffect(() => {
    if (!pending) return undefined;

    const timer = setTimeout(
      () => clearPendingNote(appointmentId),
      Math.max(0, pending.savedAt + PENDING_NOTE_WINDOW_MS - Date.now())
    );

    return () => clearTimeout(timer);
  }, [pending, appointmentId]);

  return (
    <AppointmentDetailsPanel
      {...rest}
      appointment={appointment}
      open={open}
      notes={state}
      savingNote={status === "saving" && pending ? pending.note : null}
      onReloadNotes={reload}
      {...flags}
      renderPatientLink={renderPatientLink}
    />
  );
}
